import { Injectable } from '@angular/core';
import {
  AddonHookValidationRules,
  ProposalsHooksService,
  TemplateValidationRules,
} from '../proposals-hooks.service';
import {
  ApplicationConfig,
  ApplicationConfigStep,
  ApplicationData,
  ApplicationHooksData,
  Partners,
  Proposal,
} from '@lockton-apps/shared/interfaces';
import { EnwProductConfigId } from '@lockton-apps/shared/util/constants';
import { FormGroup } from '@angular/forms';
import { compact, get, isArray, join, map, toNumber } from 'lodash';

export interface ConstructionHooksData {}

@Injectable()
export class EnwApplicationHooksService extends ProposalsHooksService<ConstructionHooksData> {
  private sharedSolicitorsAddonHookValidationRules = {
    conveyancing: {
      anyOf: [
        `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial["FY-3"]`,
        `areasOfPractice.workUndertaken.breakdown.conveyancing_residential["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.conveyancing_residential["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.conveyancing_residential["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.conveyancing_residential["FY-3"]`,
      ],
    },
    personalInjury: {
      anyOf: [
        `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant["FY-3"]`,
        `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant["FY-3"]`,
      ],
    },
    litigation: {
      anyOf: [
        `areasOfPractice.workUndertaken.breakdown.commercialLitigation["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.commercialLitigation["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.commercialLitigation["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.commercialLitigation["FY-3"]`,
        `areasOfPractice.workUndertaken.breakdown.marineLitigation["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.marineLitigation["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.marineLitigation["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.marineLitigation["FY-3"]`,
      ],
    },
    corporateCommercial: {
      anyOf: [
        `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC["FY-3"]`,
        `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other["FY-3"]`,
      ],
    },
    tax: {
      anyOf: [
        `areasOfPractice.workUndertaken.breakdown.taxPlanning["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.taxPlanning["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.taxPlanning["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.taxPlanning["FY-3"]`,
      ],
    },
    financialServices: {
      anyOf: [
        `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA["FY-3"]`,
        `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA["FY-1"].amount`,
        `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA["FY-1"].percentage`,
        `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA["FY-2"]`,
        `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA["FY-3"]`,
      ],
    },
  };

  override data: ApplicationHooksData = {
    deactivateDialog: this.deactivateDialogData,
  };

  override addonHookValidationRules: AddonHookValidationRules = {
    [`${Partners.ENW}-${EnwProductConfigId.Solicitors}`]:
      this.sharedSolicitorsAddonHookValidationRules,
    [`${Partners.ENW}-${EnwProductConfigId.SolicitorsHdi}`]:
      this.sharedSolicitorsAddonHookValidationRules,
    [`${Partners.ENW}-${EnwProductConfigId.Solicitors2025}`]:
      this.sharedSolicitorsAddonHookValidationRules,
    [`${Partners.ENW}-${EnwProductConfigId.MT}`]: {
      conveyancing: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_residential.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_residential.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_residential.FY-1.amount`,
        ],
      },
      personalInjury: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-1.amount`,
        ],
      },
    },
    [`${Partners.ENW}-${EnwProductConfigId.QBE}`]: {
      property: {
        anyOf: [
          `work.fees.breakdown.estateAgencyAndPropertyRelated.FY-1.percentage`,
          `work.fees.breakdown.estateAgencyAndPropertyRelated.FY-2`,
          `work.fees.breakdown.estateAgencyAndPropertyRelated.FY-3`,
        ],
      },
      matrimonial: {
        anyOf: [
          `work.fees.breakdown.matrimonialFamily.FY-1.percentage`,
          `work.fees.breakdown.matrimonialFamily.FY-2`,
          `work.fees.breakdown.matrimonialFamily.FY-3`,
        ],
      },
      injury: {
        anyOf: [
          `work.fees.breakdown.personalInjury_claimant.FY-1.percentage`,
          `work.fees.breakdown.personalInjury_claimant.FY-2`,
          `work.fees.breakdown.personalInjury_claimant.FY-3`,
          `work.fees.breakdown.personalInjury_defendant.FY-1.percentage`,
          `work.fees.breakdown.personalInjury_defendant.FY-2`,
          `work.fees.breakdown.personalInjury_defendant.FY-3`,
        ],
      },
      wills: {
        anyOf: [
          `work.fees.breakdown.wills.FY-1.percentage`,
          `work.fees.breakdown.wills.FY-2`,
          `work.fees.breakdown.wills.FY-3`,
        ],
      },
    },
    [`${Partners.ENW}-${EnwProductConfigId.SolePractitioner}`]: {
      conveyancing: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_commercial.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_residential.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_residential.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.conveyancing_residential.FY-1.amount`,
        ],
      },
      personalInjuryQuestionnaire: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-1.amount`,
        ],
      },
      litigationQuestionnaire: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.commercialLitigation.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.commercialLitigation.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.commercialLitigation.FY-1.amount`,
        ],
      },
      corporateQuestionnaire: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other.FY-1.amount`,
        ],
      },
      tax: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.taxPlanning.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.taxPlanning.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.taxPlanning.FY-1.amount`,
        ],
      },
      financialServices: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA.FY-1.amount`,
        ],
      },
    },
    [`${Partners.ENW}-${EnwProductConfigId.Major}`]: {
      conveyancing: {
        anyOf: [
          {
            path: 'areasOfPractice.workUndertaken.breakdown',
            iteratorFn: (v) => {
              const conveyancingCommercialFy1 = toNumber(
                get(v, 'conveyancing_commercial.FY-1.percentage', 0)
              );
              const conveyancingResidentialFy1 = toNumber(
                get(v, 'conveyancing_residential.FY-1.percentage', 0)
              );
              const conveyancingFy1 =
                conveyancingCommercialFy1 + conveyancingResidentialFy1;
              return conveyancingFy1 >= 20.5;
            },
          },
        ],
      },
      personalInjury: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-3`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_claimant.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-3`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.personalInjury_defendant.FY-1.amount`,
        ],
      },
      litigation: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.commercialLitigation.FY-3`,
          `areasOfPractice.workUndertaken.breakdown.commercialLitigation.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.commercialLitigation.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.commercialLitigation.FY-1.amount`,
        ],
      },
      corporateCommercial: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC.FY-3`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_PLC.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other.FY-3`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.commercialCorporate_other.FY-1.amount`,
        ],
      },
      tax: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.taxPlanning.FY-3`,
          `areasOfPractice.workUndertaken.breakdown.taxPlanning.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.taxPlanning.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.taxPlanning.FY-1.amount`,
        ],
      },
      financialServices: {
        anyOf: [
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA.FY-3`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_FCA.FY-1.amount`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA.FY-3`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA.FY-2`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA.FY-1.percentage`,
          `areasOfPractice.workUndertaken.breakdown.financialAdviceAndServices_SRA.FY-1.amount`,
        ],
      },
    },
    [`${Partners.ENW}-${EnwProductConfigId.MajorShort}`]: {
      conveyancing: {
        anyOf: [
          {
            path: 'areasOfPractice.workUndertaken.breakdown',
            iteratorFn: (v) => {
              const conveyancingCommercialFy1 = toNumber(
                get(v, 'conveyancing_commercial.FY-1.percentage', 0)
              );
              const conveyancingResidentialFy1 = toNumber(
                get(v, 'conveyancing_residential.FY-1.percentage', 0)
              );
              const conveyancingFy1 =
                conveyancingCommercialFy1 + conveyancingResidentialFy1;
              return conveyancingFy1 >= 20.5;
            },
          },
        ],
      },
    },
    [`${Partners.ENW}-${EnwProductConfigId.Endurance}`]: {
      conveyancing: {
        anyOf: [
          {
            path: 'areasOfPractice.workUndertaken.breakdown',
            iteratorFn: (v) => {
              const convCommFy2 = toNumber(
                get(v, 'conveyancing_commercial.FY-2', 0)
              );
              const convCommFy1 = toNumber(
                get(v, 'conveyancing_commercial.FY-1.percentage', 0)
              );
              const convResFy2 = toNumber(
                get(v, 'conveyancing_residential.FY-2', 0)
              );
              const convResFy1 = toNumber(
                get(v, 'conveyancing_residential.FY-1.percentage', 0)
              );

              const convFy2 = convCommFy2 + convResFy2;
              const convFy1 = convCommFy1 + convResFy1;

              return convFy2 > 15 || convFy1 > 15;
            },
          },
        ],
      },
    },
  };

  override templateValidationRules: TemplateValidationRules = {
    [`${Partners.ENW}-${EnwProductConfigId.Major}`]: {
      overseasOffice: {
        path: 'details.offices.other',
        iteratorFn: (values, stepConfig) =>
          this.otherOfficeTemplateIterator(values, stepConfig),
      },
    },
    [`${Partners.ENW}-${EnwProductConfigId.MajorShort}`]: {
      overseasOffice: {
        path: 'details.offices.other',
        iteratorFn: (values, stepConfig) =>
          this.otherOfficeTemplateIterator(values, stepConfig),
      },
    },
  };

  constructor() {
    super();
  }

  override afterInit(proposal: Proposal, config: ApplicationConfig): Proposal {
    return super.handleDynamicSteps(proposal.formData.data, proposal, config);
  }

  async beforeSave(
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ): Promise<Proposal> {
    return super.handleDynamicSteps(data, proposal, config);
  }

  async beforeStepSubmit(
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ): Promise<Proposal> {
    return super.handleDynamicSteps(data, proposal, config);
  }

  private otherOfficeTemplateIterator(
    values: unknown,
    stepConfig: ApplicationConfigStep
  ) {
    if (!isArray(values)) return [];

    const overseasValues = values.filter(
      (value) => get(value, 'location', '') === 'Overseas'
    );

    return map(overseasValues, (value) => ({
      step: stepConfig,
      identifier: get(value, 'id', ''),
      iteratorLabel: join(
        compact([
          get(value, ['address', 'street']),
          get(value, ['address', 'country']),
        ]),
        ', '
      ),
    }));
  }
}
